import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, RotateCcw } from "lucide-react";
import {
  Form,
  Row,
  Col,
  Container,
  Button,
  Card,
  Image,
} from "react-bootstrap";

import indiaFlag from "../assets/images/india-flag.png";
import uaeFlag from "../assets/images/uae-flag.png";
import usaFlag from "../assets/images/usa-flag.png";
import rwandaFlag from "../assets/images/rwanda-flag.png";
import arrowwithMap from "../assets/images/arrow-with-map.png";
import LayerFour from "../assets/images/layers/Layer_4.png";
import enquirySuccess from "../assets/images/enquiry-success.png";
import { apiUrl, apiHeaders } from "../apiConfig";
import "../assets/css/contact.css";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    company: "",
    message: "",
    role: "", // New role field
    subscribe: "yes", // New subscription field
    heardAbout: "",
    jobTitle: "",
    lastName: "",
  });

  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const successRef = useRef(null);

  // Bring the thank-you panel into view (the form is taller than the panel)
  useEffect(() => {
    if (submitted && successRef.current) {
      successRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      successRef.current.focus({ preventScroll: true });
    }
  }, [submitted]);

  // Email validation regex (blocks free providers)
  const businessEmailRegex =
    /@(gmail\.com|yahoo\.com|outlook\.com|hotmail\.com|aol\.com|icloud\.com|protonmail\.com|zoho\.com|mail\.com|gmx\.com)$/i;

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (businessEmailRegex.test(formData.email)) {
      newErrors.email = "Please use a business email address";
    }

    if (!formData.mobile) {
      newErrors.mobile = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Please enter a valid 10-digit phone number";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Company name is required";
    }

    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = "Job title is required";
    }

    if (!formData.role) {
      newErrors.role = "Please select your role";
    }

    if (!formData.heardAbout) {
      newErrors.heardAbout = "Please select how you heard about us";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResponseMessage("");

    // Run validation
    if (!validateForm()) {
      setLoading(false);
      setResponseMessage("Please correct all errors in the form.");
      return;
    }

    try {
      // The server builds and sends the emails; SMTP credentials stay server-side
      const res = await fetch(apiUrl("/api/contact"), {
        method: "POST",
        headers: apiHeaders,
        body: JSON.stringify(formData),
      });
      const response = await res.json().catch(() => null);
      if (!response) {
        // Non-JSON reply: the API server is down or restarting
        setResponseMessage("We couldn’t reach our server. Please try again in a moment.");
      } else if (res.ok && response.success) {
        setResponseMessage("");
        setSubmitted(true);
        setFormData({
          name: "",
          lastName: "",
          email: "",
          mobile: "",
          company: "",
          jobTitle: "",
          message: "",
          role: "",
          subscribe: "yes",
          heardAbout: "",
        });
      } else {
        setResponseMessage(
          response.message || "Failed to send email. Please try again."
        );
      }
    } catch (error) {
      setResponseMessage("We couldn’t reach our server. Please try again in a moment.");
    }

    setLoading(false);
  };

  return (
    <>
      <section className="page-section bg-white pt-80 pb-80">
        <Container>
          <Row className="align-items-center mb-5">
            <Col col={12}>
              <div class="text-center d-flex align-items-center justify-content-center">
                <h2 class="section-heading section-heading-center fw-700 fs-1">
                  Let's start your <span className="text-ee0000">Journey</span>{" "}
                  towards{" "}
                  <span className="d-block text-00A1E0">Fruitful Outcomes</span>
                </h2>
              </div>
            </Col>
          </Row>

          <Row className="mt-5 align-ite">
            <Col lg={6}>
              <div className="pe-5">
                <Image className="w-100" src={arrowwithMap} />
                <h5 className="ls-1 fw-700 fs-16 text-252B42  mt-4">
                  Want to Know More about our Services?
                </h5>
                <p className="text-252B42 fw-400 fs-12 lh-base mb-2 ls-0">
                  Leave your details here and our team will get in touch with
                  you right away.
                </p>
                <h5 className="text-252B42 fw-400 fs-12 lh-base ls-0">
                  Together we will arrange a suitable time to discuss your
                  enquiry in more detail in a no-obligation initial meeting.
                </h5>
                <h5 className="ls-1 fw-700 fs-16 text-252B42 mt-5 mb-1">
                  You can also email your enquiry to-
                </h5>
                <h5 className="ls-1 fw-700 fs-16">
                  <a
                    className="text-decoration-none text-ee0000 ls-1 fw-700 fs-16"
                    href="mailto:connect@comsenseconsulting.com"
                  >
                    connect@comsenseconsulting.com
                  </a>
                </h5>
              </div>
              <div>
                <h4 className="text-252B42 fw-700 fs-16 bordered-text bordered-small bordered-text-start pb-0 red-underline-bottom ls-0 mt-5 mb-mt-4 mb-4">
                  Office Locations
                </h4>
                <Row className="d-flex mb-0 mb-lg-4 pe-4 mt-4">
                  <Col lg={6} md={6} xs={6} className="mb-3 mb-lg-0">
                    <Image
                      className="mb-1"
                      src={indiaFlag}
                      style={{ width: "50px" }}
                    />
                    <h5 className="text-252B42 fw-600 mt-2 mt-lg-2 fs-16">
                      INDIA
                    </h5>
                    <p className="text-252B42 fw-500 fs-12 mb-0">+91-917-567-5679</p>
                  </Col>
                  <Col lg={6} md={6} xs={6} className="mb-3 mb-lg-0">
                    <Image
                      className="mb-1"
                      src={uaeFlag}
                      style={{ width: "50px" }}
                    />
                    <h5 className="text-252B42 fw-600 mt-2 mt-lg-2 fs-16">
                      UAE
                    </h5>
                    <p className="text-252B42 fw-500 fs-12 mb-0">+971-547-134-455</p>
                  </Col>
                </Row>
                <Row className="d-flex mb-4 pe-4">
                  <Col lg={6} md={6} xs={6} className="mb-3 mb-lg-0">
                    <Image
                      className="mb-1"
                      src={usaFlag}
                      style={{ width: "50px" }}
                    />
                    <h5 className="text-252B42 fw-600 mt-2 mt-lg-2 fs-16">
                      USA
                    </h5>
                    <p className="text-252B42 fw-500 fs-12 mb-0">+1-239-383-1618</p>
                  </Col>
                  <Col lg={6} md={6} xs={6} className="mb-3 mb-lg-0">
                    <Image
                      className="mb-1"
                      src={rwandaFlag}
                      style={{ width: "50px" }}
                    />
                    <h5 className="text-252B42 fw-600 mt-2 mt-lg-2 fs-16">
                      RWANDA
                    </h5>
                    <p className="text-252B42 fw-500 fs-12 mb-0">+250-790-914-908</p>
                  </Col>
                </Row>
              </div>
            </Col>
            <Col lg={6}>
              <Card className="bg-E2F1FE position-relative overflow-hidden box-shadow-four z-0 border-0 rounded-3 p-0 p-lg-3 p-md-3 p-sm-3 pb-3 pb-lg-3">
                <Image
                  src={LayerFour}
                  className="position-absolute bottom-0 start-0 z-n1 w-75"
                />

                <Card.Body>
                  {submitted ? (
                    <div
                      ref={successRef}
                      className="enquiry-success"
                      role="status"
                      aria-live="polite"
                      tabIndex={-1}
                    >
                      <Image
                        src={enquirySuccess}
                        alt=""
                        className="enquiry-success-img"
                      />
                      <h3 className="enquiry-success-title">Thank you</h3>
                      <p className="enquiry-success-subtitle">
                        We&rsquo;ve received your enquiry.
                      </p>
                      <p className="enquiry-success-text">
                        Our team will review your details and get in touch with
                        you soon.
                      </p>
                      <div className="enquiry-success-actions">
                        <Link to="/" className="btn btn-danger enquiry-success-btn">
                          Back to Home <ArrowRight size={18} />
                        </Link>
                        <button
                          type="button"
                          className="enquiry-success-link"
                          onClick={() => setSubmitted(false)}
                        >
                          <RotateCcw size={15} /> Submit another enquiry
                        </button>
                      </div>
                    </div>
                  ) : (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col lg={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>First Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder=""
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            isInvalid={!!errors.name}
                            className="rounded-1 custom-input custom-input-two"
                          />
                          {errors.name && (
                            <Form.Control.Feedback type="invalid">
                              {errors.name}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                      <Col lg={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Last Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder=""
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            className="rounded-1 custom-input custom-input-two"
                          />
                        </Form.Group>
                      </Col>

                      <Col lg={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Company</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder=""
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="rounded-1 custom-input custom-input-two"
                          />
                        </Form.Group>
                      </Col>
                      <Col lg={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Job Title</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder=""
                            name="jobTitle"
                            value={formData.jobTitle}
                            onChange={handleChange}
                            className="rounded-1 custom-input custom-input-two"
                          />
                        </Form.Group>
                      </Col>
                      <Col lg={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="email"
                            placeholder=""
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            isInvalid={!!errors.email}
                            className="rounded-1 custom-input custom-input-two"
                          />
                          {errors.email && (
                            <Form.Control.Feedback type="invalid">
                              {errors.email}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                      <Col lg={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Phone</Form.Label>
                          <Form.Control
                            type="tel"
                            placeholder=""
                            name="mobile"
                            value={formData.mobile}
                            onChange={(e) => {
                              const value = e.target.value
                                .replace(/[^\d]/g, "")
                                .slice(0, 10);
                              setFormData({ ...formData, mobile: value });
                            }}
                            isInvalid={!!errors.mobile}
                            className="rounded-1 custom-input custom-input-two"
                          />
                          {errors.mobile && (
                            <Form.Control.Feedback type="invalid">
                              {errors.mobile}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                      <Col lg={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>How did you hear about us?</Form.Label>
                          <Form.Select
                            name="heardAbout"
                            value={formData.heardAbout}
                            onChange={handleChange}
                            className="rounded-1 custom-input custom-input-two"
                          >
                            <option value="" disabled>
                              Select
                            </option>
                            <option value="Social Media">Social Media</option>
                            <option value="Friend">Friend</option>
                            <option value="Search Engine">Search Engine</option>
                            <option value="Advertisement">Advertisement</option>
                            <option value="Event">Event</option>
                            <option value="Other">Other</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col lg={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>What is your role?</Form.Label>
                          <Form.Select
                            name="role"
                            value={formData.role || ""}
                            onChange={handleChange}
                            className="rounded-1 custom-input custom-input-two"
                          >
                            <option value="">Select</option>
                            <option value="Developer">Developer</option>
                            <option value="Manager">Manager</option>
                            <option value="Designer">Designer</option>
                            <option value="Marketer">Marketer</option>
                            <option value="Other">Other</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col lg={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            How can we help your organization?
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder=""
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            className="rounded-1 custom-input custom-input-two"
                          />
                        </Form.Group>
                      </Col>
                      <Col lg={12}>
                        <Form.Group className="mb-3 mt-1">
                          <Form.Label className="mb-3">
                            I would like to receive emails from{" "}
                            <span className="fw-700">Comsense</span> with
                            exclusive updates on products, services, events, and
                            alerts. I may unsubscribe at any time.
                          </Form.Label>
                          <div>
                            <Form.Check
                              type="radio"
                              label="Yes"
                              name="subscribe"
                              value="yes"
                              className="d-block"
                              checked={formData.subscribe === "yes"}
                              onChange={handleChange}
                              inline
                            />
                            <Form.Check
                              type="radio"
                              label="No"
                              className="d-block"
                              name="subscribe"
                              value="no"
                              checked={formData.subscribe === "no"}
                              onChange={handleChange}
                              inline
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col lg={12}>
                        <Button
                          variant=""
                          type="submit"
                          className="mt-3 fw-600 d-block m-auto btn btn-danger btn-xl"
                          disabled={loading}
                        >
                          {loading ? "Submitting..." : "Submit"}
                        </Button>
                      </Col>
                    </Row>
                    {responseMessage && (
                      <p
                        className="mt-3 mb-0 fs-12 text-center"
                        style={{ color: loading ? "blue" : "red" }}
                      >
                        {responseMessage}
                      </p>
                    )}
                  </Form>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Contact;
